import type { ReactElement, ReactNode } from 'react'
import type { IAssetFile } from '~/types/file'
import type { IIapPrice } from '~/types/iap'
import type { IAssetOption, IIapOption, ITranslationOption } from '~/types/option'
import type { IBlockProps, IPageContainerFormValues, ITemplatePageConfig } from '~/types/template'

const IAP_TRANSLATION_PLACEHOLDERS: Record<string, keyof IIapPrice> = {
  IAP_PRICE: 'price',
  IAP_PRICE_SUBSCRIPTION_DISPLAYED_BY_MONTH: 'price',
  IAP_PRICE_SUBSCRIPTION_DISPLAYED_BY_WEEK: 'price',
  IAP_PRICE_SUBSCRIPTION_DISPLAYED_BY_YEAR: 'price',
  NB_DAYS_FREE_TRIAL: 'trial_period_days',
  SUBSCRIPTION_PERIOD: 'subscription_period',
}

const placeholderTextMap: Record<string, string> = {
  '1m': 'month',
  '1w': 'week',
  '1y': 'year',
  ONE_MONTH: 'month',
  ONE_WEEK: 'week',
  ONE_YEAR: 'year',
  P1M: 'month',
  P1W: 'week',
  P1Y: 'year',
  P3M: '3 months',
  P6M: '6 months',
  SIX_MONTHS: '6 months',
  THREE_MONTHS: '3 months',
}

const formatPlaceholderText = (value?: string | number): string => {
  return placeholderTextMap[String(value)] || String(value)
}

const formatTranslationText = (option: ITranslationOption, iap?: IIapOption): string => {
  const { params, text } = option

  if (!text) return ''

  let formattedText = text

  if (params) {
    params.forEach((param) => {
      formattedText = formattedText.replace('%s', param)
    })
  }

  if (iap && iap.price) {
    Object.entries(IAP_TRANSLATION_PLACEHOLDERS).forEach(([key, value]) => {
      const placeholderText = iap.price ? formatPlaceholderText(iap.price[value]) : ''
      formattedText = formattedText.replace(`[${key}]`, placeholderText)
    })
  }

  return formattedText
}

const parseTranslationValue = (
  option: string | ITranslationOption | null,
  values: IPageContainerFormValues,
  iapKey?: string
): string => {
  if (!option) {
    return ''
  }

  if (typeof option === 'string') return option

  const iap = values.placeholders.find(({ key }) => key === iapKey)?.value
  return formatTranslationText(option as ITranslationOption, iap as IIapOption)
}

const parseAssetValue = (option: IAssetOption | null): IAssetFile | undefined => {
  if (!option) return undefined
  const files = (option as IAssetOption).files || []
  return files.find(({ quality_identifier }) => quality_identifier === 'xhdpi') || files[0]
}

const parseDefaultValue = (option: string | IOption | null): string => {
  if (!option) {
    return ''
  }

  if (typeof option === 'string') return option

  return option.value
}

export const getProps = (placeholderKey: string, values: IPageContainerFormValues): IBlockProps => {
  const placeholdersFilteredByKey = values.placeholders.filter(({ key }) => key === placeholderKey)

  return placeholdersFilteredByKey.reduce((prev, { className, iapKey, key, type, value }) => {
    prev.className = `${key} ${values.focus === key ? 'is-focus' : ''} ${className || ''}`

    switch (type) {
      case 'translation':
        prev[type] = parseTranslationValue(value as ITranslationOption, values, iapKey)
        break
      case 'asset':
        prev[type] = parseAssetValue(value as IAssetOption)
        break
      case 'embeddedUi':
        break
      default:
        prev[type] = parseDefaultValue(value as string | IOption)
    }
    return prev
  }, {} as IBlockProps)
}

export const getChildren = ({
  currentPage,
  exclude,
  onClick,
  values,
}: {
  currentPage: ITemplatePageConfig
  exclude?: string | string[]
  onClick?: (placeholderKey: string) => void
  values: IPageContainerFormValues
}): ReactNode => {
  const { templateFilter } = values

  return currentPage.placeholders
    .filter(({ placeholderKey }) => !exclude?.includes(placeholderKey))
    .filter(({ filters }) => {
      if (filters && templateFilter) {
        return filters.includes(templateFilter)
      }
      return true
    })
    .map((child, index) => {
      const { placeholderKey, previewProperties } = child
      const props = getProps(placeholderKey, values)

      return (
        <child.component
          {...props}
          key={`${child.placeholderKey}-${index}`}
          onClick={() => onClick && onClick(placeholderKey)}
          properties={previewProperties}
        />
      )
    })
}

export const EmptyBlock = (): ReactElement => <></>
